import { useState, useMemo, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../components/Layout";
import { api, CATEGORIES, getCategoryLabel, QUALITIES, UNIT_CODES, getUnitLabel, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nProvider";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";
import MapPicker from "../components/MapPicker";

export default function AddListing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);

  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "Wood",
    quality: "B-grade",
    quantity: 1,
    unit: "pcs",
    weight_per_unit_kg: "",
    location: "",
    pickup_available: true,
    delivery_available: false,
    price: 0,
    image_paths: [],
    lat: null,
    lng: null,
    ursprung_projekt: "",
    destination: "intern",
    konto: "",
    publicerad: true,
  });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [orgSub, setOrgSub] = useState(null);
  const [loadingExisting, setLoadingExisting] = useState(isEdit);

  useEffect(() => {
    if (!user) return;
    api.get("/org/me").then((r) => setOrgSub(r.data?.subscription || "hybrid")).catch(() => setOrgSub("hybrid"));
  }, [user]);

  useEffect(() => {
    if (!isEdit || !user) return;
    let cancelled = false;
    api.get(`/listings/${editId}`)
      .then((r) => {
        if (cancelled) return;
        const d = r.data;
        setForm({
          title: d.title || "",
          description: d.description || "",
          category: d.category || "Wood",
          quality: d.quality || "B-grade",
          quantity: d.quantity ?? 1,
          unit: d.unit || "pcs",
          weight_per_unit_kg: d.weight_per_unit_kg ?? "",
          location: d.location || "",
          pickup_available: d.pickup_available ?? true,
          delivery_available: d.delivery_available ?? false,
          price: d.price ?? 0,
          image_paths: d.image_paths || [],
          lat: d.lat ?? null,
          lng: d.lng ?? null,
          ursprung_projekt: d.ursprung_projekt || "",
          destination: d.destination || "intern",
          konto: d.konto || "",
          publicerad: d.publicerad ?? true,
        });
      })
      .catch(() => { toast.error(t("detail.notFound")); navigate("/marketplace"); })
      .finally(() => { if (!cancelled) setLoadingExisting(false); });
    return () => { cancelled = true; };
  }, [isEdit, editId, user, navigate, t]);

  const mapValue = useMemo(() => ({ lat: form.lat, lng: form.lng }), [form.lat, form.lng]);

  // CO₂e per-kg categories — the listing needs an explicit weight per unit
  // whenever the chosen unit isn't already a mass unit.
  const PER_KG_CATEGORIES = ["Wood", "Concrete", "Metal", "Glass"];
  const MASS_UNITS = ["kg", "ton"];
  const needsWeight = useMemo(
    () => PER_KG_CATEGORIES.includes(form.category) && !MASS_UNITS.includes(form.unit),
    [form.category, form.unit]
  );

  // Live CO₂e preview shown inside the weight block once a weight is entered.
  const CO2E_FACTORS = { Wood: 0.45, Concrete: 0.12, Metal: 2.8, Glass: 0.85 };
  const weightPreview = useMemo(() => {
    if (!needsWeight) return null;
    const w = Number(form.weight_per_unit_kg);
    const q = Number(form.quantity);
    if (!w || w <= 0 || !q || q <= 0) return null;
    return (q * w * (CO2E_FACTORS[form.category] || 0)).toFixed(1);
  }, [needsWeight, form.weight_per_unit_kg, form.quantity, form.category]);

  if (!user) {
    return <Layout><div className="max-w-[1440px] mx-auto p-10 label-tech">{t("add.loginRequired")}</div></Layout>;
  }
  if (user.role !== "seller" && user.role !== "admin") {
    return (
      <Layout>
        <div className="max-w-[800px] mx-auto p-10">
          <div className="label-tech">{t("add.roleRequiredLabel")}</div>
          <h1 className="font-display text-3xl mt-2">{t("add.roleRequired")}</h1>
          <button onClick={() => navigate("/dashboard")} className="btn-primary mt-6" data-testid="goto-dashboard-btn">{t("add.goDashboard")}</button>
        </div>
      </Layout>
    );
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const paths = [];
      for (const f of files) {
        const data = new FormData();
        data.append("file", f);
        const { data: res } = await api.post("/upload", data, { headers: { "Content-Type": "multipart/form-data" } });
        paths.push(res.path);
      }
      set("image_paths", [...form.image_paths, ...paths]);
      toast.success(`${paths.length} ${t("add.uploadOk")}`);
    } catch {
      toast.error(t("add.uploadFail"));
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (path) => set("image_paths", form.image_paths.filter((p) => p !== path));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim() || !form.location.trim()) {
      toast.error(t("add.fillErr"));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        quantity: Number(form.quantity),
        price: Number(form.price),
        weight_per_unit_kg:
          form.weight_per_unit_kg === "" || form.weight_per_unit_kg == null
            ? null
            : Number(form.weight_per_unit_kg),
      };
      if (isEdit) {
        const { data } = await api.put(`/listings/${editId}`, payload);
        toast.success(t("add.updateOk"));
        navigate(`/listings/${data.id}`, { state: { justSaved: true } });
      } else {
        const { data } = await api.post("/listings", payload);
        toast.success(t("add.publishOk"));
        navigate(`/listings/${data.id}`, { state: { justSaved: true } });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t(isEdit ? "add.updateFail" : "add.publishFail"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-[900px] mx-auto px-6 pt-10 pb-16">
        <div className="label-tech">{isEdit ? t("add.editLabel") : t("add.label")}</div>
        <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tighter mt-2">{isEdit ? t("add.editTitle") : t("add.title")}</h1>

        {loadingExisting ? (
          <div className="mt-8 label-tech">{t("detail.loading")}</div>
        ) : (
        <form onSubmit={submit} className="mt-8 border border-[var(--ink-200)] bg-[var(--surface)]">
          <div className="p-6 border-b border-[var(--ink-200)]">
            <div className="label-tech mb-3">{t("add.photos")}</div>
            <label className="border-2 border-dashed border-[var(--ink-200)] hover:border-[var(--ink)] cursor-pointer p-8 flex flex-col items-center justify-center text-center transition-colors" data-testid="image-upload-label">
              <Upload size={24} />
              <div className="mt-2 text-sm">{uploading ? t("add.uploading") : t("add.dropPhotos")}</div>
              <div className="label-tech mt-1">{t("add.photoHint")}</div>
              <input type="file" accept="image/*" multiple className="hidden" onChange={onUpload} disabled={uploading} data-testid="image-upload-input" />
            </label>

            {form.image_paths.length > 0 && (
              <div className="grid grid-cols-3 md:grid-cols-5 gap-px bg-[var(--ink-200)] border border-[var(--ink-200)] mt-4">
                {form.image_paths.map((p) => (
                  <div key={p} className="relative aspect-square bg-[var(--surface-alt)]">
                    <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removeImage(p)} className="absolute top-1 right-1 bg-[var(--ink)] text-[var(--bone)] p-1" data-testid={`remove-image-btn`}>
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="md:col-span-2">
              <div className="label-tech mb-2">{t("add.titleField")}</div>
              <input type="text" className="input-tech" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder={t("add.titlePh")} data-testid="listing-title-input" required />
            </div>
            <div className="md:col-span-2">
              <div className="label-tech mb-2">{t("add.descField")}</div>
              <textarea rows={4} className="input-tech" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder={t("add.descPh")} data-testid="listing-description-input" required />
            </div>
            <div>
              <div className="label-tech mb-2">{t("add.categoryField")}</div>
              <select className="input-tech" value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="listing-category-select">
                {CATEGORIES.map((c) => <option key={c} value={c}>{getCategoryLabel(c, lang)}</option>)}
              </select>
            </div>
            <div>
              <div className="label-tech mb-2">{t("add.qualityField")}</div>
              <select className="input-tech" value={form.quality} onChange={(e) => set("quality", e.target.value)} data-testid="listing-quality-select">
                {QUALITIES.map((q) => <option key={q} value={q}>{q}</option>)}
              </select>
            </div>
            <div>
              <div className="label-tech mb-2">{t("add.quantityField")}</div>
              <input type="number" min="0" step="0.1" className="input-tech" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} data-testid="listing-quantity-input" required />
            </div>
            <div>
              <div className="label-tech mb-2">{t("add.unitField")}</div>
              <select className="input-tech" value={form.unit} onChange={(e) => set("unit", e.target.value)} data-testid="listing-unit-select">
                {UNIT_CODES.map((u) => <option key={u} value={u}>{getUnitLabel(u, lang)}</option>)}
              </select>
            </div>
            {needsWeight && (
              <div className="md:col-span-2 border border-[var(--clay)]/40 bg-[var(--clay)]/5 p-4" data-testid="weight-per-unit-block">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="label-tech text-[var(--clay-hover)]">{t("add.weightLabel")}</div>
                    <div className="text-sm text-[var(--ink-700)] mt-1 leading-relaxed">
                      {t("add.weightHelp").replace("{unit}", getUnitLabel(form.unit, lang))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="input-tech w-32"
                      placeholder="0.0"
                      value={form.weight_per_unit_kg}
                      onChange={(e) => set("weight_per_unit_kg", e.target.value)}
                      data-testid="listing-weight-input"
                    />
                    <span className="label-tech">kg / {getUnitLabel(form.unit, lang)}</span>
                  </div>
                </div>
                {weightPreview != null && (
                  <div className="mt-3 label-tech text-[var(--ink-500)]" data-testid="weight-preview">
                    {t("add.weightPreview")
                      .replace("{qty}", form.quantity)
                      .replace("{unit}", getUnitLabel(form.unit, lang))
                      .replace("{weight}", form.weight_per_unit_kg)
                      .replace("{co2e}", weightPreview)}
                  </div>
                )}
              </div>
            )}
            <div className="md:col-span-2">
              <div className="label-tech mb-2">{t("add.locationField")}</div>
              <input type="text" className="input-tech" value={form.location} onChange={(e) => set("location", e.target.value)} placeholder={t("add.locationPh")} data-testid="listing-location-input" required />
            </div>
            <div className="md:col-span-2">
              <div className="label-tech mb-2">{t("add.mapLabel")}</div>
              <MapPicker
                value={mapValue}
                onChange={({ lat, lng, location }) => {
                  setForm((f) => ({ ...f, lat, lng, location: location || f.location }));
                }}
              />
            </div>
            <div>
              <div className="label-tech mb-2">{t("add.priceField")}</div>
              <input type="number" min="0" step="0.01" className="input-tech" value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="listing-price-input" />
            </div>
            <div>
              <div className="label-tech mb-2">{t("add.handover")}</div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.pickup_available} onChange={(e) => set("pickup_available", e.target.checked)} data-testid="listing-pickup-checkbox" />
                  {t("add.pickup")}
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.delivery_available} onChange={(e) => set("delivery_available", e.target.checked)} data-testid="listing-delivery-checkbox" />
                  {t("add.delivery")}
                </label>
              </div>
            </div>
          </div>

          <div className="p-6 border-t border-[var(--ink-200)] grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="trace-section">
            <div className="md:col-span-2 label-tech">{t("add.traceTitle")}</div>
            <div className="md:col-span-2">
              <div className="label-tech mb-2">{t("add.ursprung")}</div>
              <input type="text" className="input-tech" value={form.ursprung_projekt} onChange={(e) => set("ursprung_projekt", e.target.value)} placeholder={t("add.ursprungPh")} data-testid="listing-ursprung-input" />
            </div>
            <div>
              <div className="label-tech mb-2">{t("add.destination")}</div>
              <select className="input-tech" value={form.destination} onChange={(e) => set("destination", e.target.value)} data-testid="listing-destination-select">
                <option value="intern">{t("add.destIntern")}</option>
                <option value="extern">{t("add.destExtern")}</option>
              </select>
            </div>
            {(orgSub === "intern" || orgSub === "hybrid") && (
              <div>
                <div className="label-tech mb-2">{t("add.konto")}</div>
                <input type="text" className="input-tech" value={form.konto} onChange={(e) => set("konto", e.target.value)} placeholder={t("add.kontoPh")} data-testid="listing-konto-input" />
              </div>
            )}
            {orgSub !== "intern" ? (
              <label className="md:col-span-2 flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.publicerad} onChange={(e) => set("publicerad", e.target.checked)} data-testid="listing-publicerad-checkbox" />
                {t("add.publicerad")}
              </label>
            ) : (
              <div className="md:col-span-2 label-tech" data-testid="listing-publicerad-hint">{t("add.publiceradHintIntern")}</div>
            )}
          </div>

          <div className="p-6 border-t border-[var(--ink-200)] flex items-center justify-between">
            <button type="button" onClick={() => navigate(-1)} className="btn-ghost" data-testid="listing-cancel-btn">{t("add.cancel")}</button>
            <button type="submit" disabled={submitting} className="btn-primary" data-testid="listing-submit-btn">
              {submitting ? (isEdit ? t("add.saving") : t("add.publishing")) : (isEdit ? t("add.saveChanges") : t("add.publish"))}
            </button>
          </div>
        </form>
        )}
      </div>
    </Layout>
  );
}
